import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import apiService from '../../services/ApiService';
import { Button } from '../ui/Button';
import { EmptyState } from '../ui/EmptyState';
import { PageLoader } from '../ui/PageLoader';
import { PageHeader } from '../ui/PageHeader';
import { BadgeRenderer } from '../badges/BadgeRenderer';
import PageBackLink from '../ui/PageBackLink';
import { EntityLogo } from '../ui/EntityLogo';
import { useAuth } from '../../contexts/AuthContext';
import { updateMetaTags } from '../../utils/metaTags';
import { vehicleProfilePath } from '../../utils/vehicleUrls';
import CommunityPostFeed from '../community/CommunityPostFeed';
import type { CommunityPost } from '../../types/communityPosts';

const ClubProfilePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    try {
      const result = await apiService.getClub(slug);
      setData(result);
      setError(null);
      const club = result?.club;
      if (club) {
        const image =
          (club.cover_path && (apiService.getFileUrl(club.cover_path) || club.cover_path)) ||
          (club.logo_path && (apiService.getFileUrl(club.logo_path) || club.logo_path)) ||
          `${window.location.origin}/images/brand/og-default.jpg`;
        updateMetaTags({
          title: `${club.name} | Bilt:Spec`,
          description: club.description || `${club.name} on Bilt:Spec`,
          image,
          url: window.location.href,
        });
      }
    } catch (e: any) {
      setError(e.message || 'Club not found');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <PageLoader message="Loading club" />;
  if (error || !data) {
    return (
      <div className="page max-w-3xl mx-auto px-4 py-16">
        <PageBackLink label="Back to Clubs" to="/clubs" className="mb-6" />
        <EmptyState
          title="Club not found"
          description={error || 'This club may be private or no longer listed.'}
          action={
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button type="button" variant="primary" className="min-h-[44px]" onClick={() => void load()}>
                Try again
              </Button>
              <Button type="button" variant="secondary" className="min-h-[44px]" onClick={() => navigate('/clubs')}>
                Browse clubs
              </Button>
            </div>
          }
        />
      </div>
    );
  }

  const club = data.club;
  const membership = data.membership;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-gray-100">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageBackLink label="Back to Clubs" to="/clubs" className="mb-4" />
        <div className="flex items-start gap-4 mb-2">
          {club.logo_path ? (
            <EntityLogo
              src={club.logo_path}
              alt=""
              rounded="lg"
              className="w-[90px] h-[90px] shrink-0"
            />
          ) : (
            <div
              className="w-[90px] h-[90px] rounded-xl bg-gradient-brand-tile flex items-center justify-center text-2xl font-bold text-white shrink-0"
              aria-hidden
            >
              {(club.name || '?').charAt(0).toUpperCase()}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <PageHeader
              className="mb-0"
              eyebrow="Club"
              title={club.name}
              description={
                <>
                  {club.is_verified ? 'Verified club' : 'Unverified club'}
                  {club.region ? ` · ${club.region}` : ''}
                  {club.description ? (
                    <span className="block text-gray-300 mt-3 max-w-2xl">{club.description}</span>
                  ) : null}
                </>
              }
              actions={
                <div className="flex flex-col gap-2 min-w-[160px]">
                  {isAuthenticated && (!membership || membership.status === 'former') ? (
                    <Button
                      type="button"
                      variant="primary"
                      data-testid="club-request-membership"
                      disabled={busy}
                      onClick={async () => {
                        setBusy(true);
                        try {
                          await apiService.requestClubMembership(club.id);
                          await load();
                        } catch (e: any) {
                          setError(e.message || 'Request failed');
                        } finally {
                          setBusy(false);
                        }
                      }}
                    >
                      Request membership
                    </Button>
                  ) : null}
                  {membership?.status === 'pending' ? (
                    <span
                      className="text-sm text-amber-300 self-center"
                      data-testid="club-membership-pending"
                    >
                      Membership pending
                    </span>
                  ) : null}
                  {membership?.status === 'active' ? (
                    <span
                      className="text-sm text-gray-400 capitalize self-center"
                      data-testid="club-membership-active"
                    >
                      {membership.role}
                    </span>
                  ) : null}
              {data.can_manage ? (
                <Button
                  type="button"
                  variant="secondary"
                  data-testid="club-manage-open"
                  onClick={() => navigate(`/clubs/${club.slug}/manage`)}
                >
                  Settings
                </Button>
              ) : null}
            </div>
          }
        />
          </div>
        </div>

        {club.website ? (
          <div className="mb-8 text-sm">
            <a
              href={club.website}
              className="text-orange-400 hover:text-orange-300 transition-colors duration-200"
              target="_blank"
              rel="noreferrer"
            >
              Website
            </a>
          </div>
        ) : null}

        <div className="mb-10">
          <CommunityPostFeed
            scope="club"
            entityId={Number(club.id)}
            canView={Boolean(data.can_view_updates)}
            canPost={Boolean(data.can_post_updates)}
            initialItems={(data.updates?.items || []) as CommunityPost[]}
            initialTotal={Number(data.updates?.total || 0)}
            lockedHint="Request membership and get approved to read club updates."
            emptyMemberHint={
              data.can_post_updates
                ? 'Post meetups, announcements, and notes your members will actually see.'
                : 'When club leads post, updates show up here.'
            }
          />
        </div>

        <section className="mb-10">
          <h2 className="heading-section text-lg sm:text-xl mb-4">Featured builds</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {(data.vehicles || []).map((v: any) => (
              <Link
                key={v.vehicle_id || v.id}
                to={vehicleProfilePath(v.owner_username, v.public_slug)}
                className="card-interactive p-4 cursor-pointer transition-colors duration-200"
              >
                <p className="text-white font-medium">
                  {v.year} {v.make} {v.model}
                </p>
                <p className="text-xs text-gray-500 mt-1 capitalize">{v.status}</p>
              </Link>
            ))}
            {(data.vehicles || []).length === 0 ? (
              <p className="text-gray-500 text-sm">No public vehicles linked yet.</p>
            ) : null}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="heading-section text-lg sm:text-xl mb-4">Club badges</h2>
          <div className="flex flex-wrap gap-3">
            {(data.badges || []).map((b: any) => (
              <div key={b.id} className="flex items-center gap-3 rounded-lg border border-white/[0.06] px-3 py-2">
                <BadgeRenderer design={b} size="md" />
                <div>
                  <p className="text-sm text-white">{b.name}</p>
                  <p className="text-xs text-gray-500">{b.short_label}</p>
                </div>
              </div>
            ))}
            {(data.badges || []).length === 0 ? (
              <p className="text-gray-500 text-sm">No active club badges.</p>
            ) : null}
          </div>
        </section>

        <section>
          <h2 className="heading-section text-lg sm:text-xl mb-4">Events</h2>
          <ul className="space-y-2">
            {(data.events || []).map((e: any) => (
              <li key={e.id}>
                <Link
                  to={`/events/${e.slug}`}
                  className="text-orange-400 hover:text-orange-300 transition-colors duration-200"
                >
                  {e.title}
                </Link>
                <span className="text-xs text-gray-500 ml-2 capitalize">{e.link_type}</span>
              </li>
            ))}
            {(data.events || []).length === 0 ? (
              <p className="text-gray-500 text-sm">No linked public events.</p>
            ) : null}
          </ul>
        </section>
      </div>
    </div>
  );
};

export default ClubProfilePage;
